import { useMemo } from 'react';

import { useCurrentUserWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts/useCurrentUserWorkflowTrackersContext';
import {
  WorkflowTracker,
  WorkflowTrackerKind,
  WorkflowTrackerStatus,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

export interface MemoGenerationTrackerState {
  latestTracker: WorkflowTracker;
  isInProgress: boolean;
}

export function useMemoGenerationTrackerStateByMemoId(
  householdId: string
): Record<string, MemoGenerationTrackerState> {
  const { trackers } = useCurrentUserWorkflowTrackersContext();

  return useMemo(() => {
    const memoGenerationTrackers = trackers.filter((tracker) => {
      return (
        tracker.kind === WorkflowTrackerKind.MemoGeneration &&
        tracker.householdID === householdId
      );
    });

    const stateByMemoId: Record<string, MemoGenerationTrackerState> = {};

    memoGenerationTrackers.forEach((tracker) => {
      const memoIds = getNodes(tracker.memos).map((memo) => memo.id);

      memoIds.forEach((memoId) => {
        if (stateByMemoId[memoId]) {
          return;
        }

        stateByMemoId[memoId] = {
          latestTracker: tracker,
          isInProgress: tracker.status === WorkflowTrackerStatus.Started,
        };
      });
    });

    return stateByMemoId;
  }, [householdId, trackers]);
}

export function useMemoGenerationTrackerState(
  memoId: string,
  householdId: string
): MemoGenerationTrackerState | null {
  const stateByMemoId = useMemoGenerationTrackerStateByMemoId(householdId);
  return stateByMemoId[memoId] ?? null;
}
